import * as React from "react";
import IconSearch from "../Icons/IconSearch";

import Accordion from "../Core/Accordion";
import IconTarget from "../Icons/IconTarget";
import type { Store } from "@app/interface/Stores";
import { setQueryParam } from "@app/utils/queryParams";
import { formatTime } from "@app/utils/formatTime";
import { formatKilometers } from "@app/utils/formatDistance";

type Props = {
  query: string;
  setQuery: (query: string) => void;
  error: string | null;
  loading: boolean;
  handleSearch: () => void;
  handleGeoLocate: () => void;
  countryStores: { [key: string]: { [key: string]: Store[] } };
  focusOnStore: (handle: string | null) => void;
  selectedStore: Store | undefined;
  locationSearched: boolean;
  activeStores: Store[];
};

const StoreLocator: React.FC<Props> = ({
  query,
  setQuery,
  error,
  loading,
  handleSearch,
  handleGeoLocate,
  countryStores,
  focusOnStore,
  selectedStore,
  locationSearched,
  activeStores,
}) => {
  return (
    <div className="store-locator">
      {selectedStore ? (
        <div className="store-details">
          <button
            className="store-details__back-button"
            onClick={() => {
              setQueryParam("store", null);
              focusOnStore(null);
            }}
          >
            View All Stores
          </button>
          <div className="store-details__header">
            <h2 className="h4">{selectedStore.name}</h2>

            <p>{formatKilometers(selectedStore.distance)}</p>
          </div>
          <p>{selectedStore.address.text}</p>

          <a className="store-details__phone" href={`tel:${selectedStore.phone}`}>
            {selectedStore.phone}
          </a>
          {selectedStore.open_hours && (
            <div>
              <p>Opening Hours:</p>
              <ul className="store-details__hours-list">
                {selectedStore.open_hours.map((dayObj, i) => {
                  const [day, hoursArray] = Object.entries(dayObj)[0]; // Get the day and its hours
                  const { open, close, storeClosed } = hoursArray[0];
                  return (
                    <li key={i} className="store-details__hours">
                      <p>{day}:</p>
                      <p>{storeClosed ? "Closed" : `${formatTime(open)} - ${formatTime(close)}`}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {selectedStore.distance && <p>Distance: {selectedStore.distance.toFixed(2)} km</p>}
          <a
            className="store-details__directions"
            href={`https://www.google.com/maps/dir/?api=1&destination=${selectedStore.address.latitude},${selectedStore.address.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Directions
          </a>
        </div>
      ) : (
        <>
          <div className="store-locator__header">
            <p className="h5">Store Locator</p>
            <div className="store-locator__search">
              <input
                name="search"
                placeholder="Enter location or post code"
                className=""
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
                disabled={loading}
              />
              <div className="store-locator__actions">
                <button
                  onClick={handleGeoLocate}
                  disabled={loading}
                  type="button"
                  className=""
                  aria-label="Geo Locate Me"
                >
                  <IconTarget />
                </button>
                <button onClick={handleSearch} disabled={loading} type="button" className="" aria-label="Store Search">
                  <IconSearch />
                </button>
              </div>
            </div>

            {error && <p className="error-message">{error}</p>}
          </div>

          <div className="store-locator__list">
            <div className="store-locator__list-content">
              {locationSearched ? (
                <div className="store-locator__list-active-stores">
                  {activeStores.map((store) => (
                    <StoreDetail key={store.store_id} store={store} focusOnStore={focusOnStore} />
                  ))}
                </div>
              ) : (
                <div>
                  {Object.keys(countryStores).map((country) => {
                    if (country === "NULL") return null; // Skip the "NULL" country
                    const countryName = country === "AU" ? "Australia" : country === "NZ" ? "New Zealand" : country;
                    return (
                      <div key={country}>
                        <p className="store-locator__country">{countryName}</p>
                        <Accordion
                          items={Object.entries(countryStores[country]).map(([state, stores]) => {
                            const content = (
                              <div>
                                {stores.map((store) => (
                                  <StoreDetail key={store.store_id} store={store} focusOnStore={focusOnStore} />
                                ))}
                              </div>
                            );

                            return {
                              title: state,
                              content,
                            };
                          })}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const StoreDetail = ({ store, focusOnStore }: { store: Store; focusOnStore: (handle: string) => void }) => {
  const handleDetailLink = () => {
    setQueryParam("store", store.handle);
  };

  return (
    <div className="store-detail" onClick={() => focusOnStore(store.handle)}>
      <div className="store-detail__header">
        <p className="store-detail__name">{store.name}</p>
        <p>{formatKilometers(store.distance)}</p>
      </div>
      <p className="store-detail__address">{store.address.text}</p>
      <div className="store-detail__actions">
        <button onClick={handleDetailLink} className="store-detail__link">
          View Store Details
        </button>
        <a
          target="_blank"
          href={`//www.google.com/maps/dir/Current+Location/${store.address.latitude},${store.address.longitude}`}
          className="store-detail__link"
        >
          Get Directions
        </a>
      </div>
    </div>
  );
};

export default StoreLocator;
